import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'src/axios';
import { APICONSTANTS } from 'src/components/Constants';

/**
 * Fetches the release notes bundled with this build.
 *
 * One request serves both consumers: the app bar needs `unread` for the dot, and
 * the dialog needs `releases` for its content — so the dot costs no extra
 * round-trip. The response is immutable for the life of the build and the server
 * marks it `private, max-age=300`.
 *
 * The server returns only the newest releases by default. The full history is
 * currently 37 releases and nothing compresses responses on the way out, so it
 * is fetched only when someone opens the archive — see `loadAll`.
 *
 * Failures are swallowed to an empty state: release notes are informational, and
 * a broken fetch must not surface an error banner over clinical work.
 */
export default function useReleaseNotes({ enabled = true } = {}) {
  const [state, setState] = useState({
    loading: enabled,
    releases: [],
    installedVersion: null,
    unread: false,
    total: 0,
    showingAll: false
  });

  const requested = useRef(false);
  const cancelled = useRef(false);

  const fetchNotes = useCallback((all) => {
    return axios
      .get(
        all
          ? `${APICONSTANTS.releaseNotes}?limit=all`
          : APICONSTANTS.releaseNotes
      )
      .then(({ data }) => {
        if (cancelled.current) return;
        setState({
          loading: false,
          releases: Array.isArray(data?.releases) ? data.releases : [],
          installedVersion: data?.installedVersion || null,
          unread: Boolean(data?.unread),
          total: Number(data?.total) || 0,
          showingAll: Boolean(all)
        });
      })
      .catch(() => {
        if (!cancelled.current) {
          setState((prev) => ({ ...prev, loading: false }));
        }
      });
  }, []);

  useEffect(() => {
    if (!enabled || requested.current) return;
    requested.current = true;
    cancelled.current = false;
    fetchNotes(false);

    return () => {
      cancelled.current = true;
    };
  }, [enabled, fetchNotes]);

  /** Fetch the rest of the history — the archive, on demand. */
  const loadAll = useCallback(() => {
    setState((prev) => ({ ...prev, loading: true }));
    return fetchNotes(true);
  }, [fetchNotes]);

  /**
   * Clears the unread cue locally first so the dot disappears on click rather
   * than after the round-trip; the write is fire-and-forget because a failed
   * stamp only means the dot returns on the next load.
   */
  const markSeen = useCallback(() => {
    setState((prev) => (prev.unread ? { ...prev, unread: false } : prev));
    axios.post(APICONSTANTS.releaseNotesSeen).catch(() => {});
  }, []);

  return { ...state, markSeen, loadAll };
}
